import React, {
  useRef,
  createContext,
  useContext,
  ReactNode,
  useState,
} from 'react';
import { useDrag } from '@use-gesture/react';
import { useSprings } from '@react-spring/web';

interface UseTabsDragOptions {
  currentIndex: number;
  totalTabs: number;
  disableDrag?: boolean;
  onTabChange: (newIndex: number) => void;
  onDragProgress?: (progress: number) => void;
}

interface DragTabContextValue {
  dragProgress: React.MutableRefObject<number>;
  currentIndex: number;
  totalTabs: number;
  calculateHeaderTranslateX: (activeIndex: number) => number;
}

const DragTabContext = createContext<DragTabContextValue | null>(null);

export const useDragTabContext = () => {
  const context = useContext(DragTabContext);
  if (!context) {
    throw new Error('useDragTabContext must be used within a DragTabProvider');
  }
  return context;
};

export const DragTabProvider = ({
  children,
  currentIndex,
  totalTabs,
  dragProgress,
}: {
  children: ReactNode;
  currentIndex: number;
  totalTabs: number;
  dragProgress: React.MutableRefObject<number>;
}) => {
  // 计算头部黑色背景块的 translateX 位置
  const calculateHeaderTranslateX = (activeIndex: number) => {
    // 计算背景块的 translateX 位置（基于标签索引，而不是百分比）
    const translateX = activeIndex * 100; // 每个标签移动 100% 的背景块宽度
    return translateX;
  };

  const value: DragTabContextValue = {
    dragProgress,
    currentIndex,
    totalTabs,
    calculateHeaderTranslateX,
  };

  return (
    <DragTabContext.Provider value={value}>{children}</DragTabContext.Provider>
  );
};

export const useTabsDrag = ({
  currentIndex,
  totalTabs,
  disableDrag,
  onTabChange,
  onDragProgress,
}: UseTabsDragOptions) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragProgress = useRef(0); // 拖拽进度 (-1 到 1)
  const direction = useRef<string | undefined>(undefined);
  // 新增：累计移动距离，解决移动端轻微垂直偏移导致的方向误判
  const totalMovement = useRef({ x: 0, y: 0 });
  // 使用 useSprings 为每个标签页创建动画
  const [springs, api] = useSprings(
    totalTabs,
    (index) => ({
      x: (index - currentIndex) * 100,
      opacity: index === currentIndex ? 1 : 0.5,
      config: { tension: 300, friction: 30 },
    }),
    [currentIndex]
  );

  // 当 currentIndex 改变时更新所有标签页的动画位置
  React.useEffect(() => {
    api.start((index) => ({
      x: (index - currentIndex) * 100,
      opacity: index === currentIndex ? 1 : 0.5,
      config: { tension: 300, friction: 50 },
    }));
  }, [currentIndex, api]);

  const throttledHandler = ({
    active,
    movement: [mx, my],
    last,
    event,
  }: any) => {
    if (!containerRef.current || disableDrag) return;
    const animationConfig = { tension: 500, friction: 25, clamp: true };
    const containerWidth = containerRef.current.offsetWidth || 375;
    const ratioValue = 0.2;

    // 1. 累计移动距离，优化方向判断（避免初始轻微垂直偏移锁定y方向）
    totalMovement.current.x += Math.abs(mx);
    totalMovement.current.y += Math.abs(my);
    const totalX = totalMovement.current.x;
    const totalY = totalMovement.current.y;

    // 动态判断方向：水平累计超过垂直+5像素时，锁定为x方向（容错移动端触摸偏差）
    if (!direction.current) {
      direction.current = totalX > totalY ? 'x' : 'y';
    } else if (direction.current === 'y' && totalX > totalY + 5) {
      direction.current = 'x';
    }

    if (active && direction.current === 'x') {
      // 2. 阻止事件冒泡+默认行为（防止父元素滚动/浏览器菜单干扰）
      event?.stopPropagation();
      event?.preventDefault();
      // 边界限制：防止在边界时过度拖拽
      let limitedMx = mx;
      // 边界限制（无弹性）
      if (
        (currentIndex === 0 && mx > 0) ||
        (currentIndex === totalTabs - 1 && mx < 0)
      ) {
        // 第一个标签页向右拖拽限制
        limitedMx = 0;
      }
      // 边界限制（弹性）
      // if (currentIndex === 0 && mx > 0) {
      //   limitedMx = mx * 0.3; // 左边界：拖拽力度减弱，有弹性反馈
      // } else if (currentIndex === totalTabs - 1 && mx < 0) {
      //   limitedMx = mx * 0.3; // 右边界：同上
      // }
      // 拖拽时实时更新所有标签页位置
      const dragOffset = (limitedMx / containerWidth) * 100;
      // 更新拖拽进度 (-1 到 1，负数表示向左拖拽，正数表示向右拖拽)
      const newProgress = Math.max(-1, Math.min(1, limitedMx / containerWidth));
      dragProgress.current = newProgress;
      onDragProgress?.(newProgress);

      api.start((index) => {
        const x = (index - currentIndex) * 100 + dragOffset;
        // 计算透明度：当前标签页保持完全不透明，其他标签页根据距离调整透明度
        const distance = Math.abs(x) / 100;
        let opacity;

        // 边界处理：确保边缘标签页不会完全消失
        const isAtLeftEdge = currentIndex === 0 && limitedMx > 0;
        const isAtRightEdge = currentIndex === totalTabs - 1 && limitedMx < 0;

        if (distance < 0.1) {
          // 当前显示的标签页，完全不透明
          opacity = 1;
        } else if (distance < 1) {
          // 相邻标签页，根据距离线性调整透明度
          opacity = Math.max(0.5, 1 - distance * 0.5);
        } else {
          // 远离的标签页，设置最小透明度，但在边界时保持可见
          opacity = isAtLeftEdge || isAtRightEdge ? 0.7 : 0.5;
        }

        // 特殊处理：当前标签页在边界时保持完全可见
        if (index === currentIndex && (isAtLeftEdge || isAtRightEdge)) {
          opacity = 1;
        }
        return { x, opacity, immediate: true };
      });
      setIsDragging(true);
    } else if (last) {
      if (direction.current !== 'x') {
        // 重置累计移动，避免影响下一次拖拽
        totalMovement.current = { x: 0, y: 0 };
        return;
      }
      // 简化的切换逻辑：确保总是有明确的目标位置
      let newIndex = currentIndex;

      // 计算拖拽比例
      const dragRatio = mx / containerWidth;

      if (dragRatio > ratioValue && currentIndex > 0) {
        // 向右拖拽且不是第一个，切换到上一个
        newIndex = currentIndex - 1;
      } else if (dragRatio < -ratioValue && currentIndex < totalTabs - 1) {
        // 向左拖拽且不是最后一个，切换到下一个
        newIndex = currentIndex + 1;
      }

      // 平滑重置进度
      const startProgress = dragProgress.current;
      const duration = 300; // 动画持续时间
      const startTime = Date.now();
      const animateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const currentProgress = startProgress * (1 - progress);
        dragProgress.current = currentProgress;
        onDragProgress?.(currentProgress);
        if (progress < 1) requestAnimationFrame(animateProgress);
        else {
          dragProgress.current = 0;
          onDragProgress?.(0);
        }
      };
      requestAnimationFrame(animateProgress);

      // 最终安全检查：确保 newIndex 在有效范围内
      newIndex = Math.max(0, Math.min(totalTabs - 1, newIndex));

      // 立即强制更新到目标位置，确保不会卡住
      api.start((index) => ({
        x: (index - newIndex) * 100,
        opacity: index === newIndex ? 1 : 0.5,
        immediate: false,
        config: animationConfig,
      }));
      // 通知父组件更新标签页
      if (newIndex !== currentIndex) {
        onDragProgress?.(0); // 确保切换后进度归0
        onTabChange(newIndex);
      }

      // 重置状态
      direction.current = undefined;
      totalMovement.current = { x: 0, y: 0 };
      setIsDragging(false);
    }
  };

  const bind = useDrag(throttledHandler, {
    threshold: 5,
    preventDefault: true, // 强制阻止浏览器默认触摸行为（如滚动）
    passive: false, //  允许preventDefault()生效，阻止滚动
    events: [
      'onTouchStart',
      'onTouchMove',
      'onTouchEnd',
      'onMouseDown',
      'onMouseMove',
      'onMouseUp',
    ],
  });

  return {
    bind,
    containerRef,
    isDragging,
    springs,
    api,
    dragProgress,
  };
};
